import type {
  APIKeyCreatedResponse,
  APIKeyResponse,
  AccountEnrollment,
  AccountOrganizationResponse,
  AccountResponse,
  AccountsCountResponse,
  AppAccountDevice,
  AppAccountResponse,
  AppResponseWithInclude,
  AvailablePlanTypeConstraint,
  BatteryDetails,
  CompareRate,
  CompareRateSpec,
  CompareResponse,
  ContentAuthor,
  ContentFaq,
  ContentFaqPreview,
  ContentFaqSearchResult,
  ContentGuide,
  ContentGuidePreview,
  ContentGuideSearchResult,
  ContentOverviewResponse,
  ContentSearchResponse,
  ContentSection,
  CreateThreadRequest,
  DashboardPlanResponse,
  DashboardProgram,
  DocumentsResponse,
  FaqResponse,
  AcceptInviteRequest as GenAcceptInviteRequest,
  AcceptInviteSignupRequest as GenAcceptInviteSignupRequest,
  ActiveAccountCount as GenActiveAccountCount,
  AppConfigurationResponse as GenAppConfigurationResponse,
  AppPowerStatsResponse as GenAppPowerStatsResponse,
  AppStatsResponse as GenAppStatsResponse,
  ChangePasswordRequest as GenChangePasswordRequest,
  ChatThreadBodyResponse as GenChatThreadBodyResponse,
  ChatThreadDetailBodyResponse as GenChatThreadDetailBodyResponse,
  CompareRateGroupCommissionRequest as GenCompareRateGroupCommissionRequest,
  CompareRequest as GenCompareRequest,
  CreateAPIKeyRequest as GenCreateAPIKeyRequest,
  CreateAppRequest as GenCreateAppRequest,
  CreateBatteryRequest as GenCreateBatteryRequest,
  CreateEVSERequest as GenCreateEVSERequest,
  CreateVehicleRequest as GenCreateVehicleRequest,
  CreateWebhookRequest as GenCreateWebhookRequest,
  CurrentActiveAccountsResponse as GenCurrentActiveAccountsResponse,
  DeleteInviteRequest as GenDeleteInviteRequest,
  EmbeddedAppTestLinkRequest as GenEmbeddedAppTestLinkRequest,
  EmbeddedAppTestLinkResponse as GenEmbeddedAppTestLinkResponse,
  ExportAccountsResponse as GenExportAccountsResponse,
  ForgotPasswordRequest as GenForgotPasswordRequest,
  InviteDetailsResponse as GenInviteDetailsResponse,
  LinkedProgram as GenLinkedProgram,
  ListAccountsQuery as GenListAccountsQuery,
  Location as GenLocation,
  LocationsChartResponse as GenLocationsChartResponse,
  LoginRequest as GenLoginRequest,
  OrganizationInviteRequest as GenOrganizationInviteRequest,
  PlanDetailResponse as GenPlanDetailResponse,
  PlanWeekDatapoint as GenPlanWeekDatapoint,
  PreliminaryAddress as GenPreliminaryAddress,
  ProgramDeviceStatusCounts as GenProgramDeviceStatusCounts,
  ProgramTerms as GenProgramTerms,
  ReconnectEmailResponse as GenReconnectEmailResponse,
  ResetPasswordRequest as GenResetPasswordRequest,
  RolesChangeRequest as GenRolesChangeRequest,
  StandaloneAppTestLinkResponse as GenStandaloneAppTestLinkResponse,
  StandaloneResponse as GenStandaloneResponse,
  UpdateAccountRequest as GenUpdateAccountRequest,
  UpdateAppConfigurationRequest as GenUpdateAppConfigurationRequest,
  UpdateAppRequest as GenUpdateAppRequest,
  UpdateEmailRequest as GenUpdateEmailRequest,
  UpdateOrganizationRequest as GenUpdateOrganizationRequest,
  UpdateRateGroupRequest as GenUpdateRateGroupRequest,
  UpdateWebhookRequest as GenUpdateWebhookRequest,
  ValidateEmailRequest as GenValidateEmailRequest,
  GuideResponse,
  InvoiceResponse,
  OrganizationInviteResponse,
  OrganizationUserResponse,
  PartnerExportResponse,
  PlanChartResponse,
  WebhookEventResponse,
  WebhookResponse,
} from "../generated/api";
import {
  AccountStatus,
  BrandMode,
  // The API reports a coarser status than the internal lifecycle; it is the
  // only device status the client ever sees, so it carries the plain name.
  PartnerDeviceStatus as DeviceStatus,
  DeviceType,
  FlowScope,
  PlanType,
  PlaybookContentIntegrationType,
  PlaybookContentPlanType,
  ProgramType,
  Role,
  StatusType,
} from "../generated/api";

// Enums directly from generated types
export {
  AccountStatus,
  BrandMode,
  DeviceStatus,
  DeviceType,
  FlowScope,
  PlanType,
  PlaybookContentIntegrationType,
  PlaybookContentPlanType,
  ProgramType,
  Role,
  StatusType,
};

/**
 * Compile time check for string unions that we need to create an equivalent
 * enum for. Ensure your enum syncs by adding this line after your enum:
 * true satisfies AssertUnionCoveredByEnum<GeneratedType['union'], `${YourEnum}`>
 */
type AssertUnionCoveredByEnum<U extends string, E extends string> = [
  U,
] extends [E]
  ? true
  : false;

export enum FeatureFlag {
  RenewalContact = "DASHBOARD_SHOW_RENEWAL_CONTACT",
  Chat = "DASHBOARD_SHOW_PLAYBOOK_CHAT",
}

export const ALL_STATUSES = "all" as const;

type AccountFilter = StatusType | typeof ALL_STATUSES;

true satisfies AssertUnionCoveredByEnum<
  AppAccountDevice["device_type"],
  `${DeviceType}`
>;

// The spec emits device manufacturers as inline string unions per endpoint (no
// named enum), so we mirror them here as enums. The co-located guards below fail
// `tsc` if the API union gains a member these enums don't cover.
export enum BatteryManufacturer {
  Tesla = "tesla",
  SolarEdge = "solaredge",
  Enphase = "enphase",
  Sigenergy = "sigenergy",
  Franklin = "franklin",
  EcoFlow = "ecoflow",
  Duracell = "duracell",
  FoxESS = "foxess",
  Sonnen = "sonnen",
  Generac = "generac",
  EPCube = "epcube",
}
true satisfies AssertUnionCoveredByEnum<
  GenCreateBatteryRequest["manufacturer"],
  `${BatteryManufacturer}`
>;

export enum EVSEManufacturer {
  Emporia = "emporia",
}
true satisfies AssertUnionCoveredByEnum<
  GenCreateEVSERequest["manufacturer"],
  `${EVSEManufacturer}`
>;

export enum VehicleManufacturer {
  Hyundai = "hyundai",
  Volvo = "volvo",
  Rivian = "rivian",
  Genesis = "genesis",
}
true satisfies AssertUnionCoveredByEnum<
  GenCreateVehicleRequest["manufacturer"],
  `${VehicleManufacturer}`
>;

export type Manufacturer =
  BatteryManufacturer | EVSEManufacturer | VehicleManufacturer;

export type Organization = AccountOrganizationResponse;

// Composite request = path param(s) + the generated request body.
export type UpdateOrganizationRequest = {
  uuid: string;
} & GenUpdateOrganizationRequest;

export type Enrollment = AccountEnrollment;

export type Location = GenLocation;

/** The address given during enrollment, before a service location resolves. */
export type PreliminaryAddress = GenPreliminaryAddress;

export type AppRequest = {
  appUUID: string;
};

export type AccountCountRequest = AppRequest & GenListAccountsQuery;

export type AccountCountResponse = AccountsCountResponse;

export type AccountFilterRequest = AccountCountRequest & {
  offset: number;
};

export type ProgramAgreementFilterRequest = AppRequest & {
  account_ids?: string[];
  offset?: number;
};

export type AccountInfoRequest = {
  appUUID: string;
  accountUUID: string;
};

export type DeviceFilterRequest = AppRequest & {
  account_ids?: string[];
  offset?: number;
};

export type DeviceSyncRequest = {
  appUUID: string;
  accountUUID: string;
  deviceUUID: string;
};

export type DeviceReconnectEmailRequest = {
  appUUID: string;
  deviceUUID: string;
};

export type AddBatteryRequest = {
  appUUID: string;
  accountUUID: string;
} & Omit<GenCreateBatteryRequest, "manufacturer"> & {
    manufacturer: BatteryManufacturer;
  };

export type AddChargerRequest = {
  appUUID: string;
  accountUUID: string;
} & Omit<GenCreateEVSERequest, "manufacturer"> & {
    manufacturer: EVSEManufacturer;
  };

export type LocationDocumentsRequest = {
  appUUID: string;
  accountUUID: string;
  locationUUID: string;
};

export type LocationDocument = DocumentsResponse;

export type Invoice = InvoiceResponse;

/** Dashboard (logged-in) user account. */
export type Account = Omit<AccountResponse, "organizations"> & {
  organizations: Organization[];
};

// `enrollment` is always present (the spec gives it a default), so it's tightened
// to required here for consumers that read it directly.
export type AppAccount = Omit<AppAccountResponse, "enrollment"> & {
  enrollment: AccountEnrollment;
};

export type ReconnectEmailResponse = GenReconnectEmailResponse;

// `device_type` is re-pointed to the runtime `DeviceType` enum (the spec types
// it as an inline `"BATTERY" | "EVSE"` union) so the app can switch on it.
export type Device = Omit<AppAccountDevice, "device_type"> & {
  device_type: DeviceType;
};

export type ProgramBatteryDetails = BatteryDetails;

export type Program = DashboardProgram;

// Omitted via include_in_schema=False, manually typed
export type AppAccountProgramAgreement = {
  account_uuid: string;
  program: Program;
  terms: GenProgramTerms;
  agreed_at: string;
  is_partner_agreement: boolean;
};

export type UpdateAccountRequest = GenUpdateAccountRequest;

export type LoginRequest = GenLoginRequest;

export type Page<T> = {
  data: T[];
  has_more: boolean;
  limit: number;
  offset: number;
  next_page_url: string | null;
};

// `getApps` requests `include=configuration`, so we use the with-include variant
// (its `configuration` is the typed `AppConfigurationResponse | null`).
export type AppResponse = AppResponseWithInclude;

export type LinkedProgram = GenLinkedProgram;

// `getAppPlans` returns the lean `DashboardPlanResponse`; `getPlan` returns
// `PlanDetailResponse` (which adds `plan_type`). Both make `linked_programs`
// required (the spec types it optional, but the backend always returns a list).
export type PlanResponse = Omit<DashboardPlanResponse, "linked_programs"> & {
  linked_programs: LinkedProgram[];
};

export type PlanTypeContraint = AvailablePlanTypeConstraint;

export type PlanDetailResponse = Omit<
  GenPlanDetailResponse,
  "linked_programs"
> & {
  linked_programs: LinkedProgram[];
};

export type CreateAppRequest = {
  orgSlug: string;
} & GenCreateAppRequest;

export type UpdateAppRequest = {
  uuid: string;
} & GenUpdateAppRequest;

export type CreateEmailValidationRequest = {
  email: string;
};

export type AcceptInviteRequest = GenAcceptInviteRequest;

export type AcceptInviteSignupRequest = GenAcceptInviteSignupRequest;

export type OrganizationUser = OrganizationUserResponse;

// Compose the generated body, overriding only `roles` (the spec types it as
// `string[]`; the app uses the `Role` enum).
export type OrganizationUserRolesUpdateRequest = {
  userUUID: string;
} & Omit<GenRolesChangeRequest, "roles"> & { roles: Role[] };

export type UpdateEmailRequest = GenUpdateEmailRequest;

export type InviteUser = OrganizationInviteResponse;

// Compose the generated body, overriding only `roles` (the spec types it as
// `string[] | null`; the app uses a required `Role` enum array).
export type InviteUserRequest = {
  orgSlug: string;
} & Omit<GenOrganizationInviteRequest, "roles"> & { roles: Role[] };

export type DeleteInviteRequest = {
  orgSlug: string;
} & GenDeleteInviteRequest;

export type InviteDetailsResponse = GenInviteDetailsResponse;

export type ValidateEmailRequest = GenValidateEmailRequest;

export type ChangePasswordRequest = GenChangePasswordRequest;

export type ForgotPasswordRequest = GenForgotPasswordRequest;

export type ResetPasswordRequest = GenResetPasswordRequest;

// API Keys

export type APIKey = APIKeyResponse;

export type CreateAPIKeyRequest = {
  appUUID: string;
} & GenCreateAPIKeyRequest;

export type CreateAPIKeyResponse = APIKeyCreatedResponse;

export type DeleteAPIKeyRequest = {
  appUUID: string;
  clientID: string;
};

// Webhooks

export type Webhook = WebhookResponse;

export type CreateWebhookRequest = {
  appUUID: string;
} & GenCreateWebhookRequest;

export type UpdateWebhookRequest = {
  uuid: string;
} & GenUpdateWebhookRequest;

export type WebhookEvent = WebhookEventResponse;

export type WebhookEventRequest = {
  webhookUUID: string;
  offset: number;
};

export type AppConfigurationResponse = GenAppConfigurationResponse;

export type UpdateAppConfigurationRequest = {
  appUUID: string;
} & GenUpdateAppConfigurationRequest;

export type UpdateAppConfigurationLogoRequest = {
  appUUID: string;
  logo: File;
};

export type UpdateAppConfigurationFaviconLogoRequest = {
  appUUID: string;
  favicon_logo: File;
};

export type EmbeddedAppTestLinkRequest = {
  appUUID: string;
} & GenEmbeddedAppTestLinkRequest;

export type EmbeddedAppTestLinkResponse = GenEmbeddedAppTestLinkResponse;

export type StandaloneResponse = GenStandaloneResponse;

export type StandaloneAppTestLinkResponse = GenStandaloneAppTestLinkResponse;

// compare-a-new-plan (body has plan_type/name); compare-an-existing-plan
// (body keyed by planUUID path param, no plan_type).
export type CompareAppPlanRequest = {
  appUUID: string;
} & GenCompareRequest;

export type ComparePlanRequest = {
  planUUID: string;
} & GenCompareRateGroupCommissionRequest;

export type UpdatePlanRequest = {
  planUUID: string;
} & GenUpdateRateGroupRequest;

export type ComparePlan = CompareRateSpec;

export type CompareAppRate = CompareRate;

export type CompareAppPlanResponse = CompareResponse;

export type SimulateContractExpiringRequest = AppRequest & {
  days_before_expiration: number;
};

export type ExportAccountsRequest = AppRequest & {
  filter: AccountFilter;
  search: string;
};

export type ExportAccountsResponse = GenExportAccountsResponse;

// The spec emits the export status as an inline string union on the export
// responses (no named enum), so we source the type from there and provide a
// runtime lookup for named comparisons. The `satisfies` fails `tsc` if a value
// stops matching the spec union.
export type ExportStatus = PartnerExportResponse["status"];

export const ExportStatus = {
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
} as const satisfies Record<string, ExportStatus>;

export type ExportStatusResponse = PartnerExportResponse;

export type AppStatsResponse = GenAppStatsResponse;

export type AppStatsRequest = {
  appUUID: string;
  days?: number;
};

export type AppPowerStatsResponse = GenAppPowerStatsResponse;

export type AppPowerStatsRequest = {
  appUUID: string;
};

export type LocationsChartResponse = GenLocationsChartResponse;

export type LocationsChartRequest = {
  appUUID: string;
};

export type PlanWeekDatapoint = GenPlanWeekDatapoint;

export type ActiveAccountsChartResponse = PlanChartResponse;

export type ActiveAccountsChartRequest = {
  appUUID: string;
};

export type EnrollmentsChartResponse = PlanChartResponse;

export type EnrollmentsChartRequest = {
  appUUID: string;
  days?: number;
};

export type ActiveAccountCount = GenActiveAccountCount;

export type CurrentActiveAccountsResponse = GenCurrentActiveAccountsResponse;

export type ProgramDeviceStatusCounts = GenProgramDeviceStatusCounts;

export type PlaybookContentSection = ContentSection;

export type PlaybookContentAuthor = ContentAuthor;

export type PlaybookContentFaqPreview = ContentFaqPreview;

export type PlaybookContentGuidePreview = ContentGuidePreview;

export type PlaybookContentFaq = ContentFaq;

export type PlaybookContentGuide = ContentGuide;

export type PlaybookContentOverviewResponse = ContentOverviewResponse;

export type PlaybookGuideResponse = GuideResponse;

export type PlaybookFaqResponse = FaqResponse;

export type PlaybookContentGuideSearchResult = ContentGuideSearchResult;

export type PlaybookContentFaqSearchResult = ContentFaqSearchResult;

export type PlaybookContentSearchResponse = ContentSearchResponse;

// Playbook Chat

export type ChatThreadBodyResponse = GenChatThreadBodyResponse;

export type ChatThreadDetailBodyResponse = GenChatThreadDetailBodyResponse;

export type CreateChatThreadRequest = CreateThreadRequest;

export type SendChatMessageRequest = {
  threadUUID: string;
  content: string;
};
